<script setup lang="ts">

import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import TheIcon from '@/Components/TheIcon.vue'
import { NoListingsInfoType } from '@/Pages/the-city/interfaces/City'
import { setSelectedRoomType } from '@/Composables/searchPlaceByCriteria'
import { showDatesAndScrollTop } from '@/Composables/use-scroll-&-open-search'

const props = defineProps<{
    data: NoListingsInfoType,
    isFiltersShown: boolean,
}>()

const isDorm = computed(() => props.data.roomType === 'dorm')

</script>

<template>
    <div class="mb-lg-6 mb-4 no-listings-info">
        <p class="mb-3">
            Unfortunately, there are no {{ isDorm ? 'beds in a dorm' : 'private rooms' }} available for
            your dates in {{ data.cityName }}. What can you do? <b>Here
                are 4 options.</b>
        </p>
        <div class="row no-availability-option-parent">
            <div
                class="col-xl-3 col-sm-6 mb-4 hover-animate no-availability-option-box"
                :class="{'col-xl-6': isFiltersShown}"
            >
                <div class="card shadow border-0 h-100 p-3 text-center justify-content-center" style="z-index:100">
                    <div class="card-title font-weight-bold">
                        1. Look for {{ !isDorm ? 'beds in Dorms' : 'private rooms' }}
                    </div>
                    <div class="card-body">
                        <div
                            class="switchToPrivateSearch no-listings-info-select-room-type"
                            @click="setSelectedRoomType(isDorm)"
                        >
                            {{
                                isDorm
                                    ? 'No Dorm Beds available for your dates. Check here for Private Rooms'
                                    : 'No Private Rooms available for your dates. Check here for Dorm Beds'
                            }}
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="col-xl-3 col-sm-6 mb-4 hover-animate no-availability-option-box"
                :class="{'col-xl-6': isFiltersShown}"
            >
                <div class="card shadow border-0 h-100 p-3 text-center justify-content-center" style="z-index:100">
                    <div class="card-title font-weight-bold">
                        2. Select another date
                    </div>
                    <div class="card-body">
                        <div class="cursor-pointer" @click="showDatesAndScrollTop">
                            <TheIcon
                                icon-id="calendar"
                                width="24"
                                height="24"
                                class="mr-1"
                            />
                            <p class="no-listings-info-select-data-text">
                                Select another date
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="col-xl-3 col-sm-6 mb-4 hover-animate no-availability-option-box"
                :class="{'col-xl-6': isFiltersShown}"
            >
                <div
                    v-if="data?.nearByCities"
                    class="card shadow border-0 h-100 p-3 text-center justify-content-center"
                    style="z-index:100"
                >
                    <div class="card-title font-weight-bold">
                        3. Stay in a town nearby
                    </div>
                    <div class="card-body flex-column text-left">
                        <p v-for="(item, key) in data?.nearByCities" :key class="mb-2">
                            <Link
                                :href="item.city.url"
                                :title="`Hostels in ${item.city.name}`"
                            >
                                {{ item.city.name }}
                            </Link>
                            <span class="ml-1 text-xs">
                                ({{ item.distance.km }} km / {{ item.distance.mi }} mi.)
                            </span>
                        </p>
                    </div>
                </div>
            </div>
            <div
                class="col-xl-3 col-sm-6 mb-4 hover-animate no-availability-option-box"
                :class="{'col-xl-6': isFiltersShown}"
            >
                <div class="card shadow border-0 h-100 p-3 text-center justify-content-center" style="z-index:100">
                    <div class="card-title font-weight-bold">
                        4. Consider These Booking Sites
                    </div>

                    <div class="card-body flex-column">
                        <p class="mt-0 font-weight-normal text-sm">
                            Check Hostelworld
                        </p>
                        <p>
                            <a
                                class="btn  btn-danger"
                                target="_blank"
                                rel="nofollow"
                                href="https://www.hostelz.com/hwNA"
                                title="Hostelworld"
                                onclick="gtag('event', 'click', {'event_category': 'No Availability', 'event_label': '1st option: Hostelworld.com'})"
                            >Hostelworld.com</a>
                        </p>
                        <p class="mt-0 font-weight-normal text-sm">
                            For Cheap Aparments
                        </p>
                        <p>
                            <a
                                href="https://www.hostelz.com/vrboNA"
                                class="btn btn-danger"
                                target="_blank"
                                rel="nofollow"
                                title="VRBO"
                                onclick="gtag('event', 'click', {'event_category': 'No Availability', 'event_label': '2nd option: VRBO.com', 'event_value': 'from ' + data.cityName})"
                            >Vrbo.com</a>
                        </p>
                        <p class="mt-0 font-weight-normal text-sm">
                            Consider Hotels with
                        </p>
                        <p>
                            <a
                                href="https://www.hostelz.com/bookingcomNA"
                                class="btn m-2 btn-danger"
                                target="_blank"
                                rel="nofollow"
                                title="Booking.com"
                                onclick="gtag('event', 'click', {'event_category': 'No Availability', 'event_label': '3rd option: Booking.com', 'event_value': 'from ' + data.cityName})"
                            >Booking.com</a>

                            <a
                                href="https://www.hostelz.com/expediaNA"
                                class="btn m-2 btn-danger"
                                target="_blank"
                                rel="nofollow"
                                title="Expedia.com"
                                onclick="gtag('event', 'click', {'event_category': 'No Availability', 'event_label': '4th option: Expedia.com', 'event_value': 'from ' + data.cityName})"
                            >Expedia.com</a>

                            <a
                                href="https://www.hostelz.com/hotelsNA"
                                class="btn m-2 btn-danger"
                                target="_blank"
                                rel="nofollow"
                                title="Hotels.com"
                                onclick="gtag('event', 'click', {'event_category': 'No Availability', 'event_label': '5th option: Hotels.com', 'event_value': 'from ' + data.cityName})"
                            >Hotels.com</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.no-listings-info {
    &-select-data-text {
        color: #4B4DED;
        font-size: 16px;
        line-height: 22px;
        font-weight: 400;

        &:hover {
            text-decoration: underline;
            color: #FF5852;
        }
    }

    &-select-room-type {
        color: #4B4DED;
        font-size: 16px;
        line-height: 22px;
        font-weight: 400;
        cursor: pointer;

        &:hover {
            color: #FF5852;
            text-decoration: underline;
        }
    }
}
</style>